import { useState } from 'react'
import { useRouter } from 'next/router'
import { format } from 'date-fns'
import { Button, ButtonLink, DetailIcon, Rating, Title } from '@/components'
import useStore from '@/store'
import Image from 'next/image'

const myLoader = ({ src }) => {
  return `${process.env.NEXT_PUBLIC_IMAGEKIT_URL_ENDPOINT}/${src}`
}

import acIcon from '../../../../public/icons/3d/ac.png'
import accessibilityIcon from '../../../../public/icons/3d/accessibility.png'
import dishwasherIcon from '../../../../public/icons/3d/dishwasher.png'

import LaundryImage from '../../../../public/images/reviewicons/laundry.png'
import gasIcon from '../../../../public/icons/3d/gas.png'
import hydroIcon from '../../../../public/icons/3d/hydro.png'
import waterIcon from '../../../../public/icons/3d/water.png'
import languagesIcon from '../../../../public/icons/3d/languages.png'
import maintainIcon from '../../../../public/icons/3d/maintain.png'
import noisyIcon from '../../../../public/icons/3d/noisy.png'
import parkingIcon from '../../../../public/icons/3d/parking.png'
import pestsIcon from '../../../../public/icons/3d/pests.png'
import petsIcon from '../../../../public/icons/3d/pets.png'
import responsiveIcon from '../../../../public/icons/3d/responsive.png'

interface ConfirmProps {
  data?: any

  handleCreate: (data: any) => any
  handleBack?: () => void
  canGoBack?: boolean
}

export const ConfirmReview = ({ data, handleCreate, handleBack, canGoBack = true }: ConfirmProps) => {
  const { push } = useRouter()
  const [errorMsg, setErrorMsg] = useState('')
  const setNotification = useStore((state) => state.setNotification)

  const submitReview = async () => {
    const body = {
      listing: {
        listingType: data.listingType,
        buildingType: data.buildingType,
        unitNumber: data.unitNumber,
        streetNumber: data.streetNumber,
        streetName: data.streetName,
        city: data.city,
        country: data.country,
        region: data.region,
        neighbourhood: data.neighbourhood,
        postalzip: data.postalZip,
      },
      review: {
        content: data.content,
        rating: data.rating,
        bedrooms: data.bedrooms,
        bathrooms: data.bathrooms,
        rentPrice: data.rentPrice,
        rentFrequency: data.rentFrequency,
        shareTenancy: data.shareTenancy,
        tenancyStart: data.tenancyStart,
        tenancyEnd: data.tenancyEnd || null,
        stillTenant: data.stillTenant,
      },
      details: {
        petsWelcome: Boolean(data.petsWelcome),
        petsWelcomeStory: data.petsWelcomeStory,
        noisyNeighbours: Boolean(data.noisyNeighbours),
        noisyNeighboursStory: data.noisyNeighboursStory,
        landlordResponsive: Boolean(data.landlordResponsive),
        landlordResponsiveStory: data.landlordResponsiveStory,
        wellMaintained: Boolean(data.wellMaintained),
        wellMaintainedStory: data.wellMaintainedStory,
        pests: Boolean(data.pests),
        pestsStory: data.pestsStory,
        landlordLanguages: Boolean(data.landlordLanguages),
        landlordLanguagesStory: data.landlordLanguagesStory,
        accessibility: Boolean(data.accessibility),
        accessibilityStory: data.accessibilityStory,
        dishwasherAvailable: Boolean(data.dishwasherAvailable),
        airConditioningIncluded: Boolean(data.airConditioningIncluded),
        covidFriendly: Boolean(data.covidFriendly),
        parkingLocation: data.parkingLocation,
        parkingAvgPrice: data.parkingAvgPrice,
        laundryLocation: data.laundryLocation,
        laundryIncluded: Boolean(data.laundryIncluded),
        laundryAvgPrice: data.laundryAvgPrice,
        dryerIncluded: Boolean(data.dryerIncluded),
        dryerAvgPrice: data.dryerAvgPrice,
        gasIncluded: Boolean(data.gasIncluded),
        gasAvgPrice: data.gasAvgPrice,
        hydroIncluded: Boolean(data.hydroIncluded),
        hydroAvgPrice: data.hydroAvgPrice,
        waterIncluded: Boolean(data.waterIncluded),
        waterAvgPrice: data.waterAvgPrice,
      },
      images: data.images.map((image) => ({
        filePath: image.filePath,
        fileName: image.name,
        fileType: image.fileType,
        width: image.width,
        height: image.height,
        fileId: image.fileId,
      })),
    }
    handleCreate(body)
  }

  return (
    <div className="pt-10 sm:pt-16 pb-6 max-w-7xl mx-auto px-4">
      <div className="mb-6 pb-6 border-b border-zinc-300  dark:border-zinc-700 ">
        <Title as="h3" size="lg">
          Confirm your testimonial details
        </Title>
        <p className="mt-2 max-w-2xl text-sm dark:text-zinc-200 text-zinc-600 font-medium">
          This information will be displayed publicly so be careful what you share.
        </p>
      </div>
      <div className="">
        <div className="mt-1 flex items-center">
          <Rating value={data.rating} />
        </div>
        <p className="sr-only">{data.rating} out of 5 stars</p>
      </div>
      {!data.shareTenancy ? (
        <p className="mt-4 dark:text-zinc-300 text-zinc-500 tracking-tight font-medium text-base">
          Rented from {format(new Date(data.tenancyStart), 'MMMM dd, yyyy')}{' '}
          {data.stillTenant ? '' : `to ${format(new Date(data.tenancyEnd), 'MMMM dd, yyyy')}`}
        </p>
      ) : null}
      <div
        className="text-base text-zinc-600 dark:text-zinc-100 max-w-md "
        dangerouslySetInnerHTML={{ __html: data.content }}
      />
      <ul className="my-6 flex">
        {data.images.map((image, idx) => (
          <li key={image.name} className="relative">
            <div className="rounded-lg overflow-hidden shadow-subtle h-[150px]">
              <Image
                loader={myLoader}
                width="300"
                height="300"
                src={`tr:w-300,h-300,ofo-bottom_left,ot-Good%20Neighbour,otc-FFFFFF,ots-12,otp-12,q-80${image.filePath}`}
                className="rounded-lg mb-0 leading-none"
              />
            </div>
          </li>
        ))}
      </ul>
      <div className="grid grid-cols-2 sm:grid-cols-3 mt-6 gap-6 border-t border-zinc-300  dark:border-zinc-700  pt-6">
        <DetailIcon icon={gasIcon} theme="bg-green-200" iconClass="stroke-green-800" name="gas">
          {data.gasIncluded || data.gasAvgPrice === 0 ? 'Gas Included' : `Gas approx $${data.gasAvgPrice} per month`}
        </DetailIcon>

        <DetailIcon icon={waterIcon} theme="bg-indigo-200" iconClass="stroke-indigo-800" name="gas">
          {data.waterIncluded || data.waterAvgPrice === 0
            ? 'Water Included'
            : `Gas approx $${data.waterAvgPrice} per month`}
        </DetailIcon>

        <DetailIcon icon={hydroIcon} theme="bg-orange-200" iconClass="stroke-red-800" name="hydro">
          {data.hydroIncluded || data.hydroAvgPrice === 0
            ? 'Electricity Included'
            : `Gas approx $${data.hydroAvgPrice} per month`}
        </DetailIcon>

        <DetailIcon icon={dishwasherIcon} theme="bg-green-200" iconClass="stroke-green-800" name="dishwasher">
          {data?.dishwasherAvailable ? 'Dishwasher Included' : 'No dishwasher'}
        </DetailIcon>

        <DetailIcon icon={acIcon} theme="bg-brand-50" iconClass="stroke-brand-800" name="airconditioner">
          {data?.airConditioningIncluded ? 'Air Conditioning Included' : 'No air conditioning'}
        </DetailIcon>

        <DetailIcon icon={parkingIcon} theme="bg-emerald-200" iconClass="stroke-emerald-800" name="parking">
          {data?.parkingLocation !== 'none' ? 'Parking Available' : 'Parking not available'}
          {data?.parkingLocation !== 'none' && data?.parkingAvgPrice
            ? `: approx $${data?.parkingAvgPrice} per month`
            : null}
        </DetailIcon>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 mt-8 gap-6">
        <DetailIcon
          icon={LaundryImage}
          theme="bg-lime-200"
          iconClass="stroke-lime-800"
          name="laundry"
          metrics={
            data?.laundryIncluded ? (
              <>
                <p className="text-sm mt-1 prose text-zinc-500 dark:text-zinc-400">
                  {data?.laundryAvgPrice
                    ? `Washing Machine $${data?.laundryAvgPrice} per load`
                    : `Washing Machine included`}
                </p>
                <p className="text-sm mt-1 prose text-zinc-500 dark:text-zinc-400">
                  {data?.dryerAvgPrice ? `Dryer $${data?.dryerAvgPrice} per load` : `Dryer included`}
                </p>
              </>
            ) : null
          }
        >
          {data?.laundryIncluded ? 'Laundry Available' : 'Laundry not available'}
        </DetailIcon>

        <DetailIcon
          icon={petsIcon}
          theme="bg-yellow-200"
          iconClass="stroke-yellow-800"
          name="pets"
          metrics={
            data.petsWelcomeStory ? (
              <p className="text-sm mt-1 prose text-zinc-500 dark:text-zinc-400">{data.petsWelcomeStory}</p>
            ) : null
          }
        >
          {data.petsWelcome ? 'Pets are welcome' : 'No pets welcome'}
        </DetailIcon>

        <DetailIcon
          icon={responsiveIcon}
          theme="bg-cyan-200"
          iconClass="stroke-cyan-800"
          name="landlord"
          metrics={
            data.landlordResponsiveStory ? (
              <p className="text-sm mt-1 prose text-zinc-500 dark:text-zinc-400">{data.landlordResponsiveStory}</p>
            ) : null
          }
        >
          {data.landlordResponsive ? 'Landlord was responsive to requests' : 'Landlord was not responsive to requests'}
        </DetailIcon>

        <DetailIcon
          icon={noisyIcon}
          theme="bg-emerald-200"
          iconClass="stroke-emerald-800"
          name="noisy"
          metrics={
            data.noisyNeighboursStory ? (
              <p className="text-sm mt-1 prose text-zinc-500 dark:text-zinc-400">{data.noisyNeighboursStory}</p>
            ) : null
          }
        >
          {data.noisyNeighbours ? 'Neighbours are noisy.' : 'No noisy neighbours here.'}
        </DetailIcon>

        <DetailIcon
          icon={accessibilityIcon}
          theme="bg-lime-200"
          iconClass="stroke-lime-800"
          name="accessibility"
          metrics={
            data.accessibilityStory ? (
              <p className="text-sm mt-1 prose text-zinc-500 dark:text-zinc-400">{data.accessibilityStory}</p>
            ) : null
          }
        >
          {data.accessibility ? 'Accessibility Friendly.' : 'Not Accessiblity Friendly.'}
        </DetailIcon>

        <DetailIcon
          icon={languagesIcon}
          theme="bg-rose-200"
          iconClass="stroke-rose-800"
          name="languages"
          metrics={
            data.landlordLanguagesStory ? (
              <p className="text-sm mt-1 prose text-zinc-500 dark:text-zinc-400">{data.landlordLanguagesStory}</p>
            ) : null
          }
        >
          {data.landlordLanguages ? 'Landlord supports other languages.' : 'Landlord does not support other languages.'}
        </DetailIcon>

        <DetailIcon
          icon={pestsIcon}
          theme="bg-teal-200"
          iconClass="stroke-teal-800"
          name="pests"
          metrics={
            data.pestsStory ? (
              <p className="text-sm mt-1 prose text-zinc-500 dark:text-zinc-400">{data.pestsStory}</p>
            ) : null
          }
        >
          {data.pests ? 'There were pests, insects or unwanted animals' : 'No pests, insects or unwanted animals'}
        </DetailIcon>

        <DetailIcon
          icon={maintainIcon}
          theme="bg-amber-200"
          iconClass="stroke-amber-800"
          name="maintain"
          metrics={
            data.wellMaintainedStory ? (
              <p className="text-sm mt-1 prose text-zinc-500 dark:text-zinc-400">{data.wellMaintainedStory}</p>
            ) : null
          }
        >
          {data.wellMaintained ? 'Property was well maintained' : 'Property was not well maintained'}
        </DetailIcon>
      </div>

      <div className="space-x-4 flex border-t border-zinc-300  dark:border-zinc-700 pt-6 mt-16 justify-between">
        {canGoBack && (
          <ButtonLink type="button" onClick={handleBack}>
            Back
          </ButtonLink>
        )}
        <div className="flex items-center space-x-4">
          <Button onClick={submitReview}>Post testimonial</Button>
        </div>
      </div>
    </div>
  )
}
